<script setup lang="ts">
import type { SortKey } from '@optionway/models';
import { Award, Rocket, Tag, Wind } from 'lucide-vue-next';

const properties = defineProps<{ disabled?: boolean }>();

const { loading } = useSearchResultsQueryState();
const { loading: carbonLoading } = useCarbonEmissionQueryState();

const indexStore = useSearchResultIndexStore();
const sortOptionsStore = useSortOptionsStore();
const gtmStore = useGtmStore();

const tabsElement = useTemplateRef<HTMLDivElement>('tabsReference');
const scrollElement = useTemplateRef<HTMLDivElement>('scrollReference');
const { arrivedState } = useScroll(scrollElement);

// No CO₂ for any offer (and no longer loading) → disable the carbon sort tab.
const carbonSortDisabled = computed(() => !carbonLoading.value && !indexStore.hasCarbonEmission);
const isTabDisabled = (key: SortKey) => properties.disabled || (key === 'carbonEmission' && carbonSortDisabled.value);

const pillReady = ref(false);
const prefix = 'components.results.sortOptions';
const sortIcons = {
  bestOffer: Award,
  price: Tag,
  duration: Rocket,
  carbonEmission: Wind,
};

const selectSort = (key: SortKey) => {
  if (isTabDisabled(key) || sortOptionsStore.sortBy === key) {
    return;
  }
  sortOptionsStore.sortBy = key;
  gtmStore.trackSortChanged();
};

const updatePill = () => {
  if (!tabsElement.value) return;
  const active = tabsElement.value.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
  if (!active) return;
  tabsElement.value.style.setProperty('--pill-left', `${active.offsetLeft}px`);
  tabsElement.value.style.setProperty('--pill-width', `${active.offsetWidth}px`);
};

const handleKeydown = (event: KeyboardEvent, index: number) => {
  const options = indexStore.sortOptions;
  const keyMap: Partial<Record<string, number>> = {
    ArrowRight: (index + 1) % options.length,
    ArrowLeft: (index - 1 + options.length) % options.length,
    Home: 0,
    End: options.length - 1,
  };
  const nextIndex = keyMap[event.key];
  if (nextIndex === undefined) return;

  event.preventDefault();

  const nextOption = options[nextIndex];
  if (!nextOption || isTabDisabled(nextOption.key)) return;

  selectSort(nextOption.key);
  tabsElement.value?.querySelectorAll<HTMLElement>('[role="tab"]')[nextIndex]?.focus();
};

const scrollActiveIntoView = () => {
  if (!scrollElement.value || !tabsElement.value) return;
  const active = tabsElement.value.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
  if (!active) return;
  const scrollLeft = active.offsetLeft - scrollElement.value.offsetWidth / 2 + active.offsetWidth / 2;
  scrollElement.value.scrollTo({ left: scrollLeft, behavior: 'smooth' });
};

useResizeObserver(tabsElement, updatePill);
watch(
  () => sortOptionsStore.sortBy,
  () => {
    updatePill();
    scrollActiveIntoView();
  },
  { flush: 'post' },
);

onMounted(async () => {
  await nextTick();
  updatePill();
  await nextTick();
  pillReady.value = true;
});
</script>

<template>
  <div
    class="relative w-full"
    :class="{ 'pointer-events-none opacity-50': properties.disabled }"
  >
    <div
      ref="scrollReference"
      class="overflow-x-auto scrollbar-none"
    >
      <div class="flex min-w-max rounded-xl border border-gray-200 bg-gray-100 p-1 shadow-sm md:w-full md:min-w-0">
        <div
          ref="tabsReference"
          role="tablist"
          :aria-label="$t(`${prefix}.ariaLabel`)"
          :aria-busy="loading"
          class="relative flex flex-1 justify-between"
        >
          <div
            class="absolute inset-y-0 left-[var(--pill-left)] w-[var(--pill-width)] rounded-xl bg-white shadow-sm"
            :class="pillReady ? 'transition-all duration-200' : ''"
          />

          <button
            v-for="(option, index) in indexStore.sortOptions"
            :key="option.key"
            role="tab"
            type="button"
            :aria-selected="sortOptionsStore.sortBy === option.key"
            :aria-disabled="isTabDisabled(option.key) || undefined"
            class="relative inline-flex w-full min-w-40 cursor-pointer flex-col items-start justify-center gap-1 rounded-lg px-2 py-1.5 transition-colors duration-150 md:px-5 md:py-2"
            :class="[
              sortOptionsStore.sortBy === option.key
                ? 'font-semibold text-brand-primary-700'
                : 'font-normal text-text-secondary-600',
              isTabDisabled(option.key) && 'pointer-events-none opacity-50',
            ]"
            @click="selectSort(option.key)"
            @keydown="handleKeydown($event, index)"
          >
            <div class="flex items-center gap-1.5 overflow-hidden text-xs leading-normal md:text-base xl:text-sm">
              <component
                :is="sortIcons[option.key]"
                :stroke-width="sortOptionsStore.sortBy === option.key ? 2.5 : 2"
                aria-hidden="true"
                class="h-3.5 w-3.5 shrink-0"
              />
              <span class="truncate">{{ $t(`${prefix}.${option.key}`) }}</span>
            </div>
            <!-- Carbon tab keeps its skeleton through the async CO₂ enrichment, not only the initial search -->
            <UiSkeleton
              v-if="loading || (option.key === 'carbonEmission' && carbonLoading)"
              class="h-4 w-full"
            />
            <div
              v-else-if="option.referenceValue"
              class="flex items-center gap-1 truncate text-xs font-medium tabular-nums leading-tight md:text-sm xl:text-xs"
            >
              {{ usePriceFormat(option.referenceValue.price?.total) }}
              <UiCustomSeparatorDot
                color-class="bg-current"
                size="s"
              />
              {{ useDurationFormat(option.referenceValue.travelDuration) }}
            </div>
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="!arrivedState.right"
      class="pointer-events-none absolute inset-y-0 right-0 w-12 rounded-r-xl bg-gradient-to-l from-gray-100 to-transparent md:hidden"
    />
  </div>
</template>
